import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'

type Platform = 'android' | 'ios' | 'desktop'
type TokenState = 'loading' | 'valid' | 'used' | 'expired' | 'invalid'

interface TokenInfo {
  email: string | null
  full_name: string | null
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  if (/android/i.test(ua))           return 'android'
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  return 'desktop'
}

export default function OnboardPage() {
  const { token } = useParams<{ token: string }>()
  const platform  = detectPlatform()

  const [tokenState, setTokenState] = useState<TokenState>('loading')
  const [info, setInfo]             = useState<TokenInfo | null>(null)
  const [mobileInfo, setMobileInfo] = useState<{ android: boolean; ios: boolean; androidStore: boolean } | null>(null)
  const [shown, setShown]           = useState<Platform>(platform === 'desktop' ? 'desktop' : platform)
  const [deepLinkFired, setDeepLinkFired] = useState(false)

  // Validate token
  useEffect(() => {
    if (!token) { setTokenState('invalid'); return }
    axios.get(`/api/v1/auth/token-info?token=${token}`)
      .then((r) => { setInfo(r.data); setTokenState('valid') })
      .catch((e) => {
        const status = e?.response?.status
        if (status === 410) {
          setTokenState(e.response.data?.detail?.includes('used') ? 'used' : 'expired')
        } else {
          setTokenState('invalid')
        }
      })

    axios.get('/mobile/info').then((r) => setMobileInfo({
      android:      r.data.android.available,
      ios:          r.data.ios.available,
      androidStore: r.data.android_store.available,
    })).catch(() => {})
  }, [token])

  // Attempt deep link on mobile as soon as token is confirmed valid
  useEffect(() => {
    if (tokenState !== 'valid' || platform === 'desktop' || deepLinkFired) return
    setDeepLinkFired(true)
    window.location.href = `isoft-waterworks://activate/${token}`
  }, [tokenState, platform, token, deepLinkFired])

  if (tokenState === 'loading') {
    return (
      <Shell>
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    )
  }

  if (tokenState !== 'valid') {
    const messages: Record<string, { title: string; body: string }> = {
      used:    { title: 'Nalog je već aktiviran',    body: 'Ovaj QR kod je već iskorišćen. Prijavite se sa vašim emailom i lozinkom.' },
      expired: { title: 'Link je istekao',           body: 'Ovaj pozivni link je istekao. Kontaktirajte administratora za novi.' },
      invalid: { title: 'Nevažeći link',             body: 'Ovaj link nije validan. Proverite da li ste ispravno skenirali QR kod.' },
    }
    const m = messages[tokenState] ?? messages.invalid
    return (
      <Shell>
        <div className="w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-400" viewBox="0 -960 960 960" fill="currentColor">
              <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
            </svg>
          </div>
          <h2 className="text-white font-semibold mb-2">{m.title}</h2>
          <p className="text-gray-400 text-sm">{m.body}</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      {/* Greeting */}
      {info?.full_name && (
        <p className="text-gray-400 text-sm mb-6 text-center">
          Dobrodošli, <span className="text-white font-medium">{info.full_name}</span>
        </p>
      )}

      {/* Tab switcher — desktop only */}
      {platform === 'desktop' && (
        <div className="flex gap-1 bg-gray-800 rounded-xl p-1 mb-6">
          {(['android', 'ios', 'desktop'] as Platform[]).map((p) => (
            <button key={p} onClick={() => setShown(p)}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                shown === p ? 'bg-brand-cyan text-brand-midnight' : 'text-gray-400 hover:text-white'
              }`}>
              {p === 'android' ? 'Android' : p === 'ios' ? 'iOS' : 'QR kod'}
            </button>
          ))}
        </div>
      )}

      {/* Android */}
      {shown === 'android' && (
        <div className="w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <AndroidIcon className="w-7 h-7 text-green-400 shrink-0" />
            <p className="text-white font-semibold">Android</p>
          </div>

          {platform === 'android' && (
            <div className="bg-blue-900/30 border border-blue-700/40 rounded-xl p-3 mb-4">
              <p className="text-blue-300 text-xs">
                Ako već imate aplikaciju instaliranu, ona bi trebalo da se otvori automatski.
                Ako se to nije desilo, preuzmite je ispod.
              </p>
            </div>
          )}

          {mobileInfo?.androidStore && (
            <a href={`/mobile/download/android-store?token=${token}`}
              className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-xl transition-colors mb-4">
              <DownloadIcon className="w-5 h-5" />
              Instaliraj preko Play Store-a
            </a>
          )}

          {mobileInfo?.android ? (
            <>
              {mobileInfo?.androidStore && (
                <p className="text-center text-xs text-gray-500 mb-3">ili preuzmite direktno (rezervna opcija)</p>
              )}
              <a href={`/mobile/download/android?token=${token}`}
                className={`flex items-center justify-center gap-2 w-full font-medium py-3 rounded-xl transition-colors mb-4 ${
                  mobileInfo?.androidStore
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-200'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}>
                <DownloadIcon className="w-5 h-5" />
                Preuzmi aplikaciju
              </a>
              <div className="bg-gray-800 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-300 mb-2">Nakon instalacije</p>
                <ol className="text-xs text-gray-400 space-y-1.5 list-decimal list-inside">
                  <li>Otvori <span className="text-gray-200">Podešavanja → Bezbednost</span></li>
                  <li>Uključi <span className="text-gray-200">Instalacija iz nepoznatih izvora</span></li>
                  <li>Instaliraj preuzeti APK fajl</li>
                  <li>Ponovo skeniraj QR kod — aplikacija će se otvoriti direktno na aktivaciji</li>
                </ol>
              </div>
            </>
          ) : (
            !mobileInfo?.androidStore && (
              <p className="text-gray-400 text-sm text-center py-4">Android aplikacija uskoro dostupna.</p>
            )
          )}

          {platform !== 'desktop' && (
            <button onClick={() => setShown('ios')}
              className="mt-3 w-full text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Koristite iPhone? →
            </button>
          )}
        </div>
      )}

      {/* iOS */}
      {shown === 'ios' && (
        <div className="w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-5">
            <AppleIcon className="w-7 h-7 text-gray-300 shrink-0" />
            <p className="text-white font-semibold">iPhone / iPad</p>
          </div>

          {mobileInfo?.ios ? (
            <a href={`/mobile/download/ios?token=${token}`}
              className="flex items-center justify-center gap-2 w-full bg-gray-100 hover:bg-white text-gray-900 font-medium py-3 rounded-xl transition-colors">
              <AppleIcon className="w-5 h-5" />
              Otvori App Store
            </a>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-400 text-sm">iOS aplikacija uskoro dostupna.</p>
              <p className="text-gray-500 text-xs mt-1">Kontaktirajte administratora za pristup.</p>
            </div>
          )}

          {platform !== 'desktop' && (
            <button onClick={() => setShown('android')}
              className="mt-3 w-full text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Koristite Android? →
            </button>
          )}
        </div>
      )}

      {/* Desktop QR */}
      {shown === 'desktop' && (
        <div className="w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl p-6 text-center">
          <p className="text-sm text-gray-300 mb-4">
            Skenirajte QR kodom na mobilnom telefonu
          </p>
          <div className="inline-block bg-white rounded-xl p-3 mb-4">
            <img src={`/mobile/onboard/${token}/qr.png`} alt="QR kod" className="w-44 h-44" />
          </div>
          <p className="text-xs text-gray-500">
            Kod je jedinstven za vaš nalog — nemojte ga deliti
          </p>
        </div>
      )}

      <p className="mt-8 text-xs text-gray-600">&copy; {new Date().getFullYear()} I-SOFT d.o.o. Mionica</p>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundColor: '#0A1930' }}>
      <div className="mb-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-cyan mb-3">
          <svg className="w-8 h-8 text-brand-midnight" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M12 3v1m0 16v1M4.22 4.22l.707.707M18.364 18.364l.707.707M1 12h1m20 0h1M4.22 19.778l.707-.707M18.364 5.636l.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-white">Waterworks</h1>
        <p className="text-xs text-gray-400 mt-0.5">Aktivacija naloga</p>
      </div>
      {children}
    </div>
  )
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  )
}

function AndroidIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.523 15.341a.882.882 0 01-.882-.882.882.882 0 01.882-.882.882.882 0 01.882.882.882.882 0 01-.882.882m-11.046 0a.882.882 0 01-.882-.882.882.882 0 01.882-.882.882.882 0 01.882.882.882.882 0 01-.882.882M17.78 8.545l1.769-3.063a.369.369 0 00-.135-.504.369.369 0 00-.504.135L17.13 8.2A10.762 10.762 0 0012 7.09c-1.84 0-3.568.464-5.13 1.11L5.09 5.113a.369.369 0 00-.504-.135.369.369 0 00-.135.504L6.22 8.545C3.834 9.917 2.25 12.387 2.25 15.23h19.5c0-2.843-1.584-5.313-3.97-6.685" />
    </svg>
  )
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
    </svg>
  )
}
